<template>
  <div class="advantages">
    <div class="main-page-info-cards">
      <div v-for="card in cards" :key="card.icon" class="main-page-info-card">
        <div class="icon">
          <IconBase :name="card.icon" :color="iconColor" />
        </div>
        <div class="text">{{ card.text }}</div>
      </div>
    </div>
    <SliderNavigation
      v-if="isDesktop && currentSlide"
      :current-slide="currentSlide"
      @prev="$emit('prev')"
      @next="$emit('next')"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import ru from '@/shared/i18n/ru';
import IconBase from '@/shared/icons/IconBase.vue';
import { useScreenSize } from '@/shared/utils/breakpoints';

import SliderNavigation from './SliderNavigation.vue';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

interface Card {
  text: string;
  icon: string;
}

interface SliderItem {
  name: string;
  link: string;
  img: string;
}

defineProps<{
  currentSlide?: SliderItem;
}>();

defineEmits<{
  prev: [];
  next: [];
}>();

const { isMobile, isTablet } = useScreenSize();

const isDesktop = computed(() => !isMobile.value && !isTablet.value);

const cards = computed<Card[]>(() => [
  {
    text: t('companyAdvantages.formats'),
    icon: 'read-24-icon-mini',
  },
  {
    text: t('companyAdvantages.guides'),
    icon: 'heart-outline-24-icon-mini',
  },
  {
    text: t('companyAdvantages.nearby'),
    icon: 'globus-24-icon-mini',
  },
]);

const iconColor = computed(() => {
  return isTablet.value ? '--alias-color-icon-accent-green' : '--alias-color-icon-inverse';
});
</script>

<style lang="scss" scoped>
.advantages {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  justify-content: space-between;
}

.main-page-info-cards {
  display: flex;
  gap: 32px;

  @include adaptivity.tablet {
    gap: 16px;
  }

  @include adaptivity.mobile {
    flex-direction: column;
  }
}

.main-page-info-card {
  display: flex;
  gap: 12px;
  align-items: center;

  &:nth-child(1) {
    .text {
      max-width: 200px;

      @include adaptivity.tablet {
        max-width: 100%;
      }
    }
  }

  &:nth-child(2) {
    .text {
      max-width: 195px;

      @include adaptivity.tablet {
        max-width: 100%;
      }
    }
  }

  &:nth-child(3) {
    .text {
      max-width: 250px;

      @include adaptivity.tablet {
        max-width: 100%;
      }
    }
  }

  .text {
    color: var(--alias-color-text-inverse);

    @include typography.base-400-300-medium;

    @include adaptivity.tablet {
      @include typography.base-300-300-medium;
    }

    @include adaptivity.mobile {
      color: var(--alias-color-text-default);

      @include typography.base-400-300-medium;
    }

    @include adaptivity.tablet {
      color: var(--alias-color-text-default);
    }
  }

  @include adaptivity.tablet {
    gap: 16px;
  }

  @include adaptivity.mobile {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
}

.icon {
  display: flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
}
</style>
